import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AxiosError } from "axios";
import {
  adminApi,
  type AdminAiAction,
  type AdminAiDatasetDetail,
  type AdminAiDatasetListResult,
  type AdminAiFeature,
  type AdminAiLabel,
  type AdminUserCoachRow,
} from "api/adminApi";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Input,
  NUMERIC,
  SectionHeader,
  Select,
  TD,
  TEmpty,
  TH,
  THead,
  TRow,
  TSkeleton,
  Table,
} from "pages/admin/ui";
import {
  shiftDateInputValue,
  todayInputValue,
} from "pages/admin/user/userFormat";
import DatasetRowModal from "./DatasetRowModal";
import LoadRules from "./LoadRules";
import {
  ACTION_LABEL,
  ACTIONS_BY_FEATURE,
  FEATURES,
  LABEL_HELP,
  LABEL_TEXT,
  LABEL_TONE,
  LABELS,
  changeSummary,
  editRatioLabel,
  formatOccurredAt,
} from "./aiFormat";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";
const PAGE_SIZE = 50;
// 새 행 확인 주기 — 목록을 통째로 다시 받지 않고 건수만 묻는다
const POLL_MS = 60_000;
const COLUMN_COUNT = 6;

type Filters = {
  label: AdminAiLabel | "";
  action: AdminAiAction | "";
  adminEdited: "" | "true" | "false";
  coachId: string;
  from: string;
  to: string;
};

const defaultFilters = (): Filters => ({
  label: "",
  action: "",
  adminEdited: "",
  coachId: "",
  from: shiftDateInputValue(todayInputValue(), -29) ?? "",
  // 끝은 열어 둔다 — 오늘로 고정하면 자정이 지난 뒤 새 행이 목록·폴링에서 빠진다
  to: "",
});

/**
 * AI 관리 — 데이터셋. 코치가 AI 출력을 쓰거나 고치거나 버린 액션 하나가 한 행으로 실시간 적재된다 (서버 AiDatasetHelper).
 * 여기서는 행을 훑고, 행을 열어 정답 보완과 SFT·DPO·RL 설정을 한다. 필수 검수 흐름은 없다.
 */
export default function DatasetSection() {
  const [feature, setFeature] = useState<AdminAiFeature>("automatic_thought");
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [page, setPage] = useState(0);
  const [data, setData] = useState<AdminAiDatasetListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newCount, setNewCount] = useState(0);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [coaches, setCoaches] = useState<AdminUserCoachRow[]>([]);
  const [openId, setOpenId] = useState<number | null>(null);

  const listParam = useCallback(
    (afterId?: number) => ({
      feature,
      action: filters.action || undefined,
      label: filters.label || undefined,
      adminEdited:
        filters.adminEdited === "" ? undefined : filters.adminEdited === "true",
      coachId: filters.coachId ? Number(filters.coachId) : undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      afterId,
      page: afterId === undefined ? page : 0,
      size: PAGE_SIZE,
    }),
    [feature, filters, page]
  );

  // 탭·필터·페이지를 빠르게 바꾸면 이전 요청이 나중에 도착할 수 있다 — 마지막 요청의 응답만 반영한다 (폴링도 같은 번호를 본다)
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.aiDatasetList(listParam());
      if (seq !== requestSeq.current) return;
      if (res.code === 0 && res.result) {
        setData(res.result);
        setNewCount(0);
        setLoadedAt(new Date());
      } else {
        setData(null);
        setError(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      if (seq !== requestSeq.current) return;
      setData(null);
      const status = (e as AxiosError).response?.status;
      setError(
        status === 403 ? "권한이 없는 계정이에요" : FALLBACK_ERROR_MESSAGE
      );
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [listParam]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.userCoachList();
        if (res.code === 0) setCoaches(res.result ?? []);
      } catch (e) {
        // 코치 필터만 비어 있을 뿐 목록은 쓸 수 있다
      }
    })();
  }, []);

  // 새 행 폴링 — 첫 페이지를 보고 있을 때만. 기준은 지금 받은 행 중 가장 큰 id.
  const topId = data?.rows.reduce((max, r) => Math.max(max, r.id), 0) ?? 0;
  useEffect(() => {
    if (page !== 0) return;
    const timer = window.setInterval(async () => {
      const seq = requestSeq.current;
      try {
        // 건수만 필요하다 — 행은 1개만 받는다
        const res = await adminApi.aiDatasetList({
          ...listParam(topId),
          size: 1,
        });
        if (seq !== requestSeq.current) return;
        if (res.code === 0) setNewCount(res.result?.newCount ?? 0);
      } catch (e) {
        // 다음 주기에 다시 묻는다
      }
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [page, topId, listParam]);

  const changeFeature = (next: AdminAiFeature) => {
    setFeature(next);
    // 액션은 영역마다 달라 넘어가면 비운다
    setFilters((prev) => ({ ...prev, action: "" }));
    setPage(0);
  };

  const changeFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  // 모달에서 저장하면 목록의 보완 표시만 바꾼다 (다시 불러오면 스크롤·페이지가 흔들린다)
  const onSaved = (detail: AdminAiDatasetDetail) =>
    setData((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) =>
              r.id === detail.id
                ? {
                    ...r,
                    adminEdited: detail.adminOutput != null,
                    split: detail.split,
                  }
                : r
            ),
          }
        : prev
    );

  const rows = data?.rows ?? [];
  const totalCount = data?.totalCount ?? 0;
  const lastPage = Math.max(0, Math.ceil(totalCount / PAGE_SIZE) - 1);
  const summary = data?.summary;

  return (
    <section className="flex flex-col gap-4">
      <SectionHeader
        title="데이터셋"
        count={`${totalCount.toLocaleString()}행`}
      />

      {/* 영역 탭 */}
      <div className="flex gap-1 border-b border-slate-200">
        {FEATURES.map(({ key, label }) => (
          <button
            type="button"
            key={key}
            onClick={() => changeFeature(key)}
            className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors ${
              feature === key
                ? "border-[#0F55A7] font-semibold text-[#0F55A7]"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {label}
            <span className={`text-xs ${NUMERIC} text-slate-400`}>
              {(data?.featureCounts[key] ?? 0).toLocaleString()}
            </span>
          </button>
        ))}
      </div>

      <LoadRules feature={feature} />

      {/* 요약 — 선택한 영역 전체 기준 (필터 무관) */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "쌓인 행", value: summary?.totalCount },
          { label: "오늘 적재", value: summary?.todayCount },
          { label: "관리자 보완", value: summary?.adminEditedCount },
        ].map(({ label, value }) => (
          <Card key={label} className="px-4 py-3">
            <p className="text-xs text-slate-500">{label}</p>
            <p
              className={`mt-1 text-xl font-semibold text-slate-900 ${NUMERIC}`}
            >
              {value == null ? "–" : value.toLocaleString()}
            </p>
          </Card>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap items-end gap-2">
        <Select
          sizing="md"
          fullWidth={false}
          className="w-36"
          aria-label="라벨"
          value={filters.label}
          onChange={(e) =>
            changeFilter("label", e.target.value as Filters["label"])
          }
        >
          <option value="">라벨 전체</option>
          {LABELS.map((l) => (
            <option key={l} value={l}>
              {LABEL_TEXT[l]}
            </option>
          ))}
        </Select>
        <Select
          fullWidth={false}
          className="w-40"
          aria-label="관리자 보완"
          value={filters.adminEdited}
          onChange={(e) =>
            changeFilter(
              "adminEdited",
              e.target.value as Filters["adminEdited"]
            )
          }
        >
          <option value="">보완 여부 전체</option>
          <option value="true">보완함</option>
          <option value="false">보완 안 함</option>
        </Select>
        <Select
          fullWidth={false}
          className="w-36"
          aria-label="액션"
          value={filters.action}
          onChange={(e) =>
            changeFilter("action", e.target.value as Filters["action"])
          }
        >
          <option value="">액션 전체</option>
          {ACTIONS_BY_FEATURE[feature].map((a) => (
            <option key={a} value={a}>
              {ACTION_LABEL[a]}
            </option>
          ))}
        </Select>
        <Select
          fullWidth={false}
          className="w-40"
          aria-label="코치"
          value={filters.coachId}
          onChange={(e) => changeFilter("coachId", e.target.value)}
        >
          <option value="">전체 코치</option>
          {coaches.map((c) => (
            <option key={c.coachId} value={c.coachId}>
              {c.name}
            </option>
          ))}
        </Select>
        <Input
          type="date"
          fullWidth={false}
          className="w-40"
          aria-label="시작일"
          value={filters.from}
          onChange={(e) => changeFilter("from", e.target.value)}
        />
        <span className="pb-2 text-sm text-slate-400">~</span>
        <Input
          type="date"
          fullWidth={false}
          className="w-40"
          aria-label="종료일"
          value={filters.to}
          onChange={(e) => changeFilter("to", e.target.value)}
        />
        <span className="ml-auto pb-2 text-xs text-slate-400">
          {loadedAt &&
            `마지막 갱신 ${loadedAt.toLocaleTimeString("ko-KR", { hour12: false })}`}
        </span>
      </div>

      {newCount > 0 && (
        <button
          type="button"
          onClick={() => load()}
          className="rounded-md border border-[#0F55A7]/20 bg-[#DCEFFA] px-3 py-2 text-left text-sm font-medium text-[#0F55A7] hover:bg-[#cfe7f7]"
        >
          새 행 {newCount.toLocaleString()}건 · 눌러서 불러오기
        </button>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Table>
        <THead>
          <TH>일시</TH>
          <TH help={LABEL_HELP}>라벨</TH>
          <TH>AI 출력</TH>
          <TH>코치 저장값</TH>
          <TH>코치</TH>
          <TH>관리자</TH>
        </THead>
        <tbody>
          {loading && !data ? (
            <TSkeleton colSpan={COLUMN_COUNT} />
          ) : rows.length === 0 ? (
            <TEmpty colSpan={COLUMN_COUNT}>조건에 맞는 행이 없어요</TEmpty>
          ) : (
            rows.map((row) => {
              const change = changeSummary(row);
              return (
                <TRow key={row.id} className="cursor-pointer">
                  <TD
                    muted
                    className={NUMERIC}
                    onClick={() => setOpenId(row.id)}
                  >
                    {formatOccurredAt(row.occurredAt)}
                  </TD>
                  <TD onClick={() => setOpenId(row.id)}>
                    <span className="flex items-center gap-1.5">
                      <Badge tone={LABEL_TONE[row.label]}>
                        {LABEL_TEXT[row.label]}
                      </Badge>
                      <span className={`text-xs text-slate-400 ${NUMERIC}`}>
                        {row.label === "edited"
                          ? editRatioLabel(row.editRatio)
                          : ""}
                      </span>
                    </span>
                  </TD>
                  <TD
                    className="max-w-[360px]"
                    onClick={() => setOpenId(row.id)}
                  >
                    <HoverText text={change.ai} className="text-slate-500">
                      {change.ai || (
                        <span className="text-slate-300">(AI 없음)</span>
                      )}
                    </HoverText>
                  </TD>
                  <TD
                    className="max-w-[360px]"
                    onClick={() => setOpenId(row.id)}
                  >
                    <HoverText text={change.coach}>
                      {change.coach == null ? (
                        <span className="text-slate-300">(안 씀)</span>
                      ) : (
                        change.coach || (
                          <span className="text-slate-300">(비움)</span>
                        )
                      )}
                    </HoverText>
                  </TD>
                  <TD muted onClick={() => setOpenId(row.id)}>
                    {row.coachName ?? row.coachId ?? ""}
                  </TD>
                  <TD onClick={() => setOpenId(row.id)}>
                    <span className="flex items-center gap-1.5">
                      {row.adminEdited && <Badge tone="info">보완됨</Badge>}
                      {row.origin === "backfill" && (
                        <Badge tone="warn">추정</Badge>
                      )}
                    </span>
                  </TD>
                </TRow>
              );
            })
          )}
        </tbody>
      </Table>

      <div className="flex items-center justify-end gap-2">
        <span className={`text-sm text-slate-500 ${NUMERIC}`}>
          {page + 1} / {lastPage + 1}
        </span>
        <Button
          size="sm"
          disabled={page === 0 || loading}
          onClick={() => setPage((p) => p - 1)}
        >
          이전
        </Button>
        <Button
          size="sm"
          disabled={page >= lastPage || loading}
          onClick={() => setPage((p) => p + 1)}
        >
          다음
        </Button>
      </div>

      {openId != null && (
        <DatasetRowModal
          id={openId}
          rowIds={rows.map((r) => r.id)}
          onNavigate={setOpenId}
          onSaved={onSaved}
          onClose={() => setOpenId(null)}
        />
      )}
    </section>
  );
}

// 말줄임된 셀 — 마우스를 올리면 전체 메시지를 바로 보여준다.
// 표 컨테이너가 overflow-x-auto 라 absolute 툴팁은 잘려서, 셀 위치를 재서 fixed 로 띄운다.
// 화면 아래쪽 셀이면 위로 띄운다.
function HoverText({
  text,
  className = "",
  children,
}: {
  text: string | null | undefined;
  className?: string;
  children: ReactNode;
}) {
  const [pos, setPos] = useState<CSSProperties | null>(null);
  return (
    <p
      className={`truncate ${className}`}
      onMouseEnter={(e) => {
        if (!text) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setPos(
          rect.bottom > window.innerHeight / 2
            ? { left: rect.left, bottom: window.innerHeight - rect.top + 4 }
            : { left: rect.left, top: rect.bottom + 4 }
        );
      }}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && (
        <span
          role="tooltip"
          style={{ position: "fixed", ...pos }}
          className="z-50 max-h-80 w-[480px] max-w-[90vw] overflow-y-auto whitespace-pre-wrap break-words rounded-md bg-slate-900 px-3 py-2 text-xs font-normal leading-relaxed text-white shadow-lg"
        >
          {text}
        </span>
      )}
    </p>
  );
}
